import { c as _c } from "react/compiler-runtime";
import { Badge, type BadgeProps } from '@chakra-ui/react';
import { LANE, type CrmPalette } from '@src/components/sco/Admin/AiCrm/crmLane';
import type { CrmChannelKey, CrmFlagKind, CrmLaneKey, CrmVerdictKind } from '@src/components/sco/Admin/AiCrm/data/types';
import React from 'react';

/** The tinted, outlined pill the handoff uses for lanes, flags, and status. */
export function CrmPill(props) {
  const $ = _c(6);
  let rest;
  let tone;
  if ($[0] !== props) {
    ({
      tone,
      ...rest
    } = props);
    $[0] = props;
    $[1] = rest;
    $[2] = tone;
  } else {
    rest = $[1];
    tone = $[2];
  }
  let t0;
  if ($[3] !== rest || $[4] !== tone) {
    t0 = <Badge borderRadius="full" colorPalette={tone} fontWeight="bold" letterSpacing="wider" px={2.5} py={1} shadowColor="colorPalette.border/50" size="sm" textTransform="uppercase" variant="surface" {...rest} />;
    $[3] = rest;
    $[4] = tone;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
}

/** A smaller, squarer chip for flags and topics on a list row. */
export function CrmChip(props) {
  const $ = _c(6);
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      tone: t0,
      ...rest
    } = props);
    $[0] = props;
    $[1] = rest;
    $[2] = t0;
  } else {
    rest = $[1];
    t0 = $[2];
  }
  const tone = t0 === undefined ? "gray" : t0;
  let t1;
  if ($[3] !== rest || $[4] !== tone) {
    t1 = <Badge borderRadius="md" colorPalette={tone} fontSize="2xs" fontWeight="bold" letterSpacing="wider" px={1.5} py={0.5} shadowColor="colorPalette.border/40" textTransform="uppercase" variant="surface" {...rest} />;
    $[3] = rest;
    $[4] = tone;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
export function LanePill(props) {
  const $ = _c(8);
  let label;
  let lane;
  let rest;
  if ($[0] !== props) {
    ({
      lane,
      label,
      ...rest
    } = props);
    $[0] = props;
    $[1] = label;
    $[2] = lane;
    $[3] = rest;
  } else {
    label = $[1];
    lane = $[2];
    rest = $[3];
  }
  const meta = lane ? LANE[lane] : null;
  const t0 = meta?.palette || "gray";
  const t1 = label || meta?.label || "Not checked";
  let t2;
  if ($[4] !== rest || $[5] !== t0 || $[6] !== t1) {
    t2 = <CrmPill tone={t0} {...rest}>{t1}</CrmPill>;
    $[4] = rest;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
const VERDICT_TONE: Record<CrmVerdictKind, CrmPalette> = {
  human: 'orange',
  assist: 'purple',
  auto: 'green',
  none: 'gray',
  support: 'purple',
  unchecked: 'gray',
  unsent: 'red',
  dismissed: 'gray'
};
export function VerdictPill(props) {
  const $ = _c(8);
  let kind;
  let label;
  let rest;
  if ($[0] !== props) {
    ({
      kind,
      label,
      ...rest
    } = props);
    $[0] = props;
    $[1] = kind;
    $[2] = label;
    $[3] = rest;
  } else {
    kind = $[1];
    label = $[2];
    rest = $[3];
  }
  const t0 = VERDICT_TONE[kind];
  let t1;
  if ($[4] !== label || $[5] !== rest || $[6] !== t0) {
    t1 = <CrmPill tone={t0} {...rest}>{label}</CrmPill>;
    $[4] = label;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
const FLAG: Record<Exclude<CrmFlagKind, 'none'>, {
  tone: CrmPalette;
  label: string;
}> = {
  late: {
    tone: 'red',
    label: "We're late"
  },
  needs: {
    tone: 'yellow',
    label: 'Needs a person'
  },
  ai: {
    tone: 'green',
    label: 'AI has it'
  }
};
export function FlagChip(props) {
  const $ = _c(3);
  if (props.flag === "none") {
    return null;
  }
  const meta = FLAG[props.flag];
  let t0;
  if ($[0] !== meta.label || $[1] !== meta.tone) {
    t0 = <CrmChip tone={meta.tone}>{meta.label}</CrmChip>;
    $[0] = meta.label;
    $[1] = meta.tone;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/** "EM" and "DM" are short for the eye. The full word is the tooltip and the accessible name. */
export function ChannelChip(props) {
  const $ = _c(4);
  const label = props.channel === "email" ? "Email" : "Direct message";
  const t0 = props.channel === "email" ? "blue" : "purple";
  const t1 = props.channel === "email" ? "EM" : "DM";
  let t2;
  if ($[0] !== label || $[1] !== t0 || $[2] !== t1) {
    t2 = <CrmChip aria-label={label} role="img" title={label} tone={t0} variant="subtle">{t1}</CrmChip>;
    $[0] = label;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}